// frontend/src/components/Admin/DevicesAdminView.tsx
// STAB-01 — MÓDULO PERMANENTE de administración de dispositivos.
// Vista principal: selector de administrador (ADM-001/ADM-002), lista de
// dispositivos del admin de la sesión, agregar dispositivo (pairing T2),
// detalle + historial + acciones (revocar/activar/reemplazar).
// La identidad pertenece al ADMINISTRADOR; el tipo de dispositivo es solo
// descriptivo y nunca participa en autenticación ni reglas de negocio.
import React, { useEffect, useState, useCallback } from 'react';
import DeviceDetailModal, { DeviceSummary } from './DeviceDetailModal';
import EnrollmentModal from './EnrollmentModal';   // MP-STAB-04 — flujo por Enrollment Link (el código de pairing es interno)
import * as api from '../../services/api';   // MP-STAB-03 — todas las llamadas autenticadas pasan por requestAuth (401 → rutina global)

interface AdminInfo {
  id: string;
  name: string;
  role: string;
}

// DevicesAdminView — Administración de Dispositivos (superficie de gestión + bootstrap)
// props.bootstrap: sesión admin-session (contraseña) — SOLO re-registro (Policy Engine v3):
//   se ocultan las acciones operativas (revocar/reactivar/reemplazar) que el motor
//   deniega con RULE_DENIED (device:manage exige device-status — H1 por política).
export default function DevicesAdminView({ bootstrap = false }: { bootstrap?: boolean }) {
  const [token] = useState<string | null>(() => localStorage.getItem('kf25_operator_token'));
  const [admins, setAdmins] = useState<AdminInfo[]>([]);
  const [selectedAdmin, setSelectedAdmin] = useState<string | null>(() => localStorage.getItem('kf25_admin_id'));
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPairing, setShowPairing] = useState(false);
  const [detailDevice, setDetailDevice] = useState<DeviceSummary | null>(null);

  // Cargar administradores disponibles (identificación del módulo)
  // MP-STAB-03 — vía requestAuth: ante 401 la rutina global expulsa (sin lógica propia aquí)
  const loadAdmins = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.requestAuth('GET', '/api/auth/admins', null, token);
      setAdmins(data.admins || []);
    } catch { /* silencioso — se reintenta al seleccionar */ }
  }, [token]);

  // Establecer sesión administrativa para el admin elegido (capa única adminSession)
  // KF25-AUTH-01/02: bajo RAS (sesión device-status, dev_token_*) la identidad ya
  // está FIJADA por el dispositivo — el ritual POST /admin/session pertenece SOLO
  // al flujo legacy/bootstrap (op_token_* de contraseña). Para dispositivos
  // autenticados la selección es local (metadata); el backend sigue siendo la
  // autoridad (devices/enrollments quedan scoped al admin de la sesión).
  const selectAdmin = async (adminId: string) => {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      if (token.startsWith('dev_token_')) {
        localStorage.setItem('kf25_admin_id', adminId);
        setSelectedAdmin(adminId);
        await loadDevices();
        return;
      }
      const res = await fetch('/api/auth/admin/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, adminId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al establecer sesión');
      localStorage.setItem('kf25_admin_id', adminId);
      setSelectedAdmin(adminId);
      await loadDevices();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar dispositivos del admin de la sesión (GET /devices — solo-propios)
  const loadDevices = useCallback(async () => {
    if (!token) return;
    setError('');
    try {
      const res = await fetch('/api/auth/devices', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cargar dispositivos');
      setDevices(data.devices || []);
    } catch (e: any) {
      setError(e.message);
    }
  }, [token]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);
  useEffect(() => { if (selectedAdmin) loadDevices(); }, [selectedAdmin, loadDevices]);

  const refresh = () => { loadDevices(); };

  if (!token) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-600">Sesión de operador no encontrada. Inicia sesión para administrar dispositivos.</p>
      </div>
    );
  }

  // Selector de administrador (si no hay sesión activa)
  if (!selectedAdmin) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white border border-purple-100 rounded-2xl shadow-sm p-6 text-center">
          <div className="text-4xl mb-3">📱</div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Administración de Dispositivos</h1>
          <p className="text-sm text-gray-500 mb-6">Selecciona el administrador cuyos dispositivos deseas gestionar</p>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>}
          <div className="space-y-3">
            {admins.map(a => (
              <button
                key={a.id}
                onClick={() => selectAdmin(a.id)}
                disabled={loading}
                className="w-full px-4 py-3 bg-purple-50 border border-purple-200 text-purple-800 rounded-xl hover:bg-purple-100 transition-colors text-left disabled:opacity-50 flex items-center justify-between"
              >
                <span>
                  <span className="font-semibold">{a.name}</span>
                  <span className="block text-xs text-purple-500">{a.id} · {a.role}</span>
                </span>
                <span className="text-purple-400">→</span>
              </button>
            ))}
          </div>
          {admins.length === 0 && !loading && (
            <button onClick={loadAdmins} className="mt-4 text-sm text-purple-600 hover:underline">Reintentar carga</button>
          )}
        </div>
      </div>
    );
  }

  const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
    active: { label: 'Activo', cls: 'bg-green-100 text-green-700' },
    revoked: { label: 'Revocado', cls: 'bg-red-100 text-red-700' },
    replaced: { label: 'Reemplazado', cls: 'bg-blue-100 text-blue-700' },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📱 Dispositivos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Administrando: <strong>{admins.find(a => a.id === selectedAdmin)?.name || selectedAdmin}</strong>
            {' '}· <button onClick={() => { localStorage.removeItem('kf25_admin_id'); setSelectedAdmin(null); }} className="text-purple-600 hover:underline text-xs">cambiar</button>
          </p>
        </div>
        <button
          onClick={() => setShowPairing(true)}
          className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          + Agregar dispositivo
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>}

      {devices.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <div className="text-4xl mb-3">🔌</div>
          <p className="text-gray-600 mb-1">No hay dispositivos registrados para {selectedAdmin}</p>
          <p className="text-sm text-gray-400 mb-4">Registra un dispositivo para acceder al workspace desde él</p>
          <button onClick={() => setShowPairing(true)} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors font-medium">
            + Agregar dispositivo
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map(d => {
            const badge = STATUS_BADGE[d.status] || STATUS_BADGE.active;
            return (
              <button
                key={d.id}
                onClick={() => setDetailDevice(d)}
                className="w-full bg-white border border-gray-200 rounded-2xl p-4 hover:border-purple-300 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-2xl">{d.status === 'active' ? '🟢' : d.status === 'revoked' ? '🔴' : '🔵'}</div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{d.label || 'Dispositivo'}</p>
                      <p className="text-xs font-mono text-gray-500">
                        {d.id}
                        {d.type ? ` · ${d.type}` : ''}
                        {d.activeSessionsCount ? ` · ${d.activeSessionsCount} sesión(es) activa(s)` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    <span className="text-gray-300">›</span>
                  </div>
                </div>
                <div className="mt-2 flex gap-4 text-xs text-gray-500">
                  <span>Último uso: {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' }) : 'nunca'}</span>
                  {d.replacedBy && <span className="text-blue-600">Reemplazado por {d.replacedBy}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showPairing && selectedAdmin && (
        <EnrollmentModal
          token={token || ''}
          adminId={selectedAdmin}
          adminName={admins.find((a) => a.id === selectedAdmin)?.name || selectedAdmin}
          onClose={() => setShowPairing(false)}
        />
      )}

      {detailDevice && (
        <DeviceDetailModal
          device={detailDevice}
          token={token}
          onClose={() => setDetailDevice(null)}
          onChanged={refresh}
          bootstrap={bootstrap}
        />
      )}
    </div>
  );
}
